import * as _ from 'lodash';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import type { FunctionComponent, ReactElement, VoidFunctionComponent } from 'react';
import type { InjectedRouter } from 'react-router';

import usePackages from '../../hooks/api/packages/usePackages';
import usePackageInventorySearchParams from '../../hooks/usePackageInventorySearchParams';
import usePrevious from '../../hooks/usePrevious';
import PaginationFooter from '../generic/Pagination/PaginationFooter';
import SidebarLayout from '../layouts/SidebarLayout';
import type { RouterProps } from '../../types';

import PackageInventoryFilters from './filters';
import PackageFilters from './filters/PackageFilters';
import PackageInventorySearchAndSort from './filters/PackageSearchAndSort';
import { PackageInventoryFiltersProjectPackagesContainer as ProjectFilters } from './filters/ProjectFilters';
import VulnFilters from './filters/VulnFilters';
import useSetFirstPackageActive from './hooks/useSetFirstPackageActive';
import PackageDetails from './PackageDetails';
import PackageIndexExportButton from './PackageIndexExportButton';
import PackageInventoryCTA from './PackageInventoryCTA';

import './PackageInventory.less';
import PackageInventorySidebar from './PackageInventorySidebar';

export type PackageInventoryParams = {
  packageLocator?: string;
  version?: string;
};

type PropsWithRouter = RouterProps<PackageInventoryParams> & {
  router: InjectedRouter;
};

/**
 * Component which is rendered when the user hits either:
 *   - /packages
 *   - /packages/:packageLocator
 * In the case of `/packages`, the useSetFirstPackageActive
 * hook will select the first package (when it exists) and
 * will bring the user to /packages/:packageLocator.
 *
 * `children` here comes from react-router, and in this case is PackageDetails.
 */
const PackageInventory: VoidFunctionComponent<PropsWithRouter> = ({ location, router, params }) => {
  const searchParams = usePackageInventorySearchParams();
  const { setPage } = searchParams;
  const { data, isError, isLoading, isPreviousData } = usePackages(searchParams.params);
  const packages = data?.data ?? [];
  const previousTeamIds = usePrevious(searchParams.params.teamIds);
  const hasTeamSelectionChanged = previousTeamIds !== undefined &&
    !_.isEqual(previousTeamIds, searchParams.params.teamIds);

  useEffect(() => {
    if (hasTeamSelectionChanged) {
      setPage(1);
    }
  }, [hasTeamSelectionChanged, setPage]);

  const hasFilterApplied = Object.values(searchParams.filters).some(filter => (filter || []).length > 0);
  const hasFilterAndNoPackages = !!data && packages.length === 0 && hasFilterApplied;
  const hasNoPackages = !!data && packages.length === 0;

  //  Set the first package as active if none exists.
  useSetFirstPackageActive({ location, router, params, packages: isPreviousData ? [] : packages });

  return (
    <>
      <Helmet
        //  Renders `Package Inventory | FOSSA` if no selected package.
        title={params.packageLocator}
        titleTemplate="%s - Package Inventory | FOSSA"
        defaultTitle="Package Inventory | FOSSA"
      />
      <PackageInventoryCTA />
      <div className="container packageInventory" data-test-id="package-inventory-table">
        <SidebarLayout>
          <SidebarLayout.Main>
            <div className="package-inventory-controls">
              <PackageInventorySearchAndSort searchParams={searchParams} />
              <PackageIndexExportButton filters={searchParams.filters} />
            </div>
            <div className="package-inventory-container">
              {!isError && (
                <PackageInventorySidebar
                  packages={packages}
                  isLoading={isLoading}
                  count={searchParams.count}
                  totalItems={data?.count ?? 0}
                />
              )}
              <PackageDetails
                hasPackageListError={isError}
                hasNoPackages={hasNoPackages}
                hasFilterApplied={hasFilterApplied}
                resetFilters={searchParams.resetFilters}
                query={searchParams.params}
              />
            </div>
            {!isError && !hasFilterAndNoPackages && (
              <PaginationFooter
                page={searchParams.page}
                count={searchParams.count}
                totalItems={data?.count ?? 0}
                setPage={searchParams.setPage}
                setCount={searchParams.setCount}
              />
            )}
          </SidebarLayout.Main>
          {!isError && (
            <SidebarLayout.Sidebar>
              <PackageInventoryFilters>
                <PackageFilters filters={searchParams.filters} setFilters={searchParams.setFilters} />
                <ProjectFilters filters={searchParams.filters} setFilters={searchParams.setFilters} />
                <VulnFilters filters={searchParams.filters} setFilters={searchParams.setFilters} />
              </PackageInventoryFilters>
            </SidebarLayout.Sidebar>
          )}
        </SidebarLayout>
      </div>
    </>
  );
};

/**
 * Container component for the new package inventory.
 * As the root Route for `/packages`, its children is whatever
 * components are specified for the child <Route> components in
 * the routes.tsx file.
 *
 * For example, if the user is on `/packages/:packageLocator`,
 * this will render PackageInventory, whos `children` will be
 * PackageDetails. For `/packages/:packageLocator/:version`,
 * children is rendered directly (no <PackageInventory>),
 * meaning <PackageVersion> is rendered.
 */
const PackageInventoryContainer: FunctionComponent<PropsWithRouter> = ({ location, router, params, children }) => {
  //  If the version param exists, then there is no need for us to render the main UI.
  if (params.version) {
    return children as ReactElement;
  }

  return (
    <PackageInventory
      location={location}
      router={router}
      params={params}
    >
      {children}
    </PackageInventory>
  );
};

export default PackageInventoryContainer;
